// File: src/components/TopNav.jsx

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

import {
  IoIosSearch,
  IoIosNotificationsOutline,
} from 'react-icons/io';

import {
  IoPeopleOutline,
  IoEllipsisVertical,
} from 'react-icons/io5';

import {
  LuMessageCircle,
  LuBell,
  LuSearch,
  LuCrown,
  LuTicketCheck,
} from 'react-icons/lu';

import LocationField from './LocationField';
import theme from '../theme';

const MOBILE_NAV_BREAKPOINT = 440;

// Enable when the host marketplace is ready. This only controls navigation.
const HOST_MARKETPLACE_ENABLED = false;

function isTrueMobileDevice() {
  if (typeof navigator === 'undefined') return false;

  return /android|iphone|ipad|ipod|mobile|tablet/i.test(
    navigator.userAgent
  );
}

function getIsMobileNav() {
  if (typeof window === 'undefined') return false;

  return (
    isTrueMobileDevice() ||
    window.innerWidth <= MOBILE_NAV_BREAKPOINT
  );
}

export default function TopNav({
  variant = 'standard',

  // Common props
  onLogoClick,
  userAvatarUrl,
  isHost = false,

  // Avatar callbacks
  onProfileClick,
  onSettingsClick,
  onLogoutClick,

  // Market selection
  selectedMarketId,
  onMarketSelected,

  // Temporary compatibility with existing layout prop names.
  // This callback now receives { marketId, label, market }.
  onLocationSelected,

  // Navigation callbacks
  onDiscoverClick,
  onFriendsClick,
  onNotificationsClick,

  notificationLabel = 'Notifications',
  friendsLabel = 'Chats',
  discoverLabel = 'Discover',

  // Centered variant
  backLabel = 'Back / Something',
  onBackClick,

  // Bottom line
  lineWidth = '100%',
  lineMarginTop = 12,
  lineColor = '#eee',
  lineHeight = '1px',

  // Notifications
  notifications = [],
  unreadCount = 0,
  showNotifDropdown = false,
  onMarkAllRead = () => {},
  onMarkOneRead = () => {},
}) {
  const showHostNav = isHost && HOST_MARKETPLACE_ENABLED;

  const location = useLocation();
  const navigate = useNavigate();

  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [isMobile, setIsMobile] = useState(getIsMobileNav);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(getIsMobileNav());
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener(
        'orientationchange',
        handleResize
      );
    };
  }, []);

  const displayUnread = unreadCount > 99 ? '99' : unreadCount;

  let fullAvatarUrl = null;

  if (userAvatarUrl) {
    const isAbsolute =
      userAvatarUrl.startsWith('http://') ||
      userAvatarUrl.startsWith('https://');

    fullAvatarUrl = isAbsolute
      ? userAvatarUrl
      : `${process.env.REACT_APP_API_URL}/${userAvatarUrl.replace(
          /^\/?/,
          ''
        )}`;
  }

  const discoverSelected =
    location.pathname === '/events' ||
    location.pathname.startsWith('/matchflow');

  const friendsSelected = location.pathname === '/friends';

  const notificationsSelected =
    location.pathname === '/notifications';

  const profileSelected = location.pathname === '/profile';

  function handleMarketSelected(selection) {
    if (!selection?.marketId) return;

    const callback = onMarketSelected || onLocationSelected;
    callback?.(selection);
  }

  const handleNotificationsClick = () => {
    if (isMobile) {
      navigate('/notifications');
    } else {
      onNotificationsClick?.();
    }
  };

  const handleThreeDotsClick = () => {
    onSettingsClick?.();
    navigate('/settings');
  };

  const handleProfileClick = () => {
    if (isMobile) {
      navigate('/profile');
    } else {
      onProfileClick?.();
    }
  };

  const bottomLineStyle = {
    width: lineWidth,
    margin: `${lineMarginTop}px auto 0 auto`,
    borderBottom: `${lineHeight} solid ${lineColor}`,
  };

  function getCircleBase() {
    if (isMobile) {
      return {
        width: 36,
        height: 36,
        borderRadius: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    }

    return {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid transparent',
    };
  }

  function getIconCircleStyle(iconName, isSelected) {
    const base = getCircleBase();

    if (isMobile) {
      return {
        ...base,
        backgroundColor: isSelected ? '#f3f4f6' : '#ffff',
        color: '#000',
        transition: theme.transitions.default,
        cursor: 'pointer',
      };
    }

    const isHovered = hoveredIcon === iconName;
    const brandBlue = theme.colors.primary;
    const brandBlueHover =
      theme.colors.primaryHover || '#0069d9';

    let backgroundColor = 'transparent';
    let iconColor = theme.colors.textMain;

    if (isSelected) {
      backgroundColor = isHovered
        ? brandBlueHover
        : brandBlue;
      iconColor = '#fff';
    } else if (isHovered) {
      backgroundColor = theme.colors.highlight;
    }

    return {
      ...base,
      backgroundColor,
      color: iconColor,
      transition: theme.transitions.default,
      cursor: 'pointer',
    };
  }

  function getLabelStyle(isSelected) {
    return {
      fontFamily: theme.typography.fontFamilyBase,
      fontSize: 11.5,
      lineHeight: 1.1,
      marginTop: 6,
      letterSpacing: 0.2,
      minHeight: 14,
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      textTransform: 'none',
      userSelect: 'none',
      transition: theme.transitions.default,
      color: isSelected
        ? theme.colors.textMain || '#0b0b0b'
        : theme.colors.textSub || '#6b7280',
      fontWeight: 800,
      opacity: isSelected ? 1 : 0.92,
    };
  }

  // ------------------------------------------------------------
  // MOBILE LAYOUT
  // ------------------------------------------------------------

  if (variant === 'standard' && isMobile) {
    const row1Style = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      height: 35,
      padding: `0 ${theme.spacing.md}px`,
      gap: showHostNav ? 10 : 0,
    };

    const row1LocationContainerStyle = {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      margin: showHostNav ? '0 6px' : '0 12px',
      minWidth: 0,
    };

    const topRowIconButtonStyle = {
      width: 34,
      height: 34,
      borderRadius: 999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      position: 'relative',
      flexShrink: 0,
      background: notificationsSelected ? '#f3f4f6' : '#fff',
      color: '#000',
      marginTop: 8,
      marginRight: 4,
    };

    const threeDotsStyle = {
      fontSize: 22,
      cursor: 'pointer',
      flexShrink: 0,
    };

    const row2WrapperStyle = {
      margin: `0 ${theme.spacing.md}px`,
    };

    const row2Style = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 12,
      gap: showHostNav ? 8 : 'clamp(0px, 7vw, 100px)',
    };

    const iconWrapperStyleMobile = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      cursor: 'pointer',
      position: 'relative',
      minWidth: 50,
    };

    return (
      <div
        id="mainNavbar"
        style={{
          width: '100%',
          backgroundColor: theme.colors.background,
          fontFamily: theme.typography.fontFamilyBase,
        }}
      >
        {/* Row 1 */}
        <div style={row1Style}>
          <div
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <TribeLogo
              style={{ width: '70px', height: 'auto' }}
              onClick={onLogoClick}
            />
          </div>

          <div style={row1LocationContainerStyle}>
            <LocationField
              selectedMarketId={selectedMarketId}
              onMarketSelected={handleMarketSelected}
              compactMobile={showHostNav}
            />
          </div>

          {showHostNav && (
            <div
              style={topRowIconButtonStyle}
              onClick={handleNotificationsClick}
              onMouseEnter={() =>
                setHoveredIcon('notifications')
              }
              onMouseLeave={() => setHoveredIcon(null)}
              aria-label="Notifications"
              title="Notifications"
            >
              <LuBell style={{ fontSize: 20 }} />

              {unreadCount > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    minWidth: 17,
                    height: 17,
                    padding: '0 4px',
                    borderRadius: 999,
                    backgroundColor: 'red',
                    border: '1px solid #fff',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  {displayUnread}
                </div>
              )}
            </div>
          )}

          <IoEllipsisVertical
            style={threeDotsStyle}
            onClick={handleThreeDotsClick}
          />
        </div>

        {/* Row 2 */}
        <div style={row2WrapperStyle}>
          <div style={row2Style}>
            {/* DISCOVER */}
            <div
              style={iconWrapperStyleMobile}
              onClick={onDiscoverClick}
            >
              <div
                style={getIconCircleStyle(
                  'discover',
                  discoverSelected
                )}
                onMouseEnter={() => setHoveredIcon('discover')}
                onMouseLeave={() => setHoveredIcon(null)}
              >
                <LuSearch style={{ fontSize: 20 }} />
              </div>

              <span style={getLabelStyle(discoverSelected)}>
                {discoverLabel}
              </span>
            </div>

            {/* RESERVATIONS */}
            <div
              style={iconWrapperStyleMobile}
              onClick={() => navigate('/tickets')}
            >
              <div
                style={getIconCircleStyle(
                  'tickets',
                  location.pathname === '/tickets'
                )}
                onMouseEnter={() => setHoveredIcon('tickets')}
                onMouseLeave={() => setHoveredIcon(null)}
              >
                <LuTicketCheck style={{ fontSize: 20 }} />
              </div>

              <span
                style={getLabelStyle(
                  location.pathname === '/tickets'
                )}
              >
                Reservations
              </span>
            </div>

            {/* CHATS */}
            <div
              style={iconWrapperStyleMobile}
              onClick={onFriendsClick}
            >
              <div
                style={getIconCircleStyle(
                  'friends',
                  friendsSelected
                )}
                onMouseEnter={() => setHoveredIcon('friends')}
                onMouseLeave={() => setHoveredIcon(null)}
              >
                <LuMessageCircle style={{ fontSize: 20 }} />
              </div>

              <span style={getLabelStyle(friendsSelected)}>
                {friendsLabel}
              </span>
            </div>

            {/* HOST */}
            {showHostNav && (
              <div
                style={iconWrapperStyleMobile}
                onClick={() => navigate('/host')}
              >
                <div
                  style={getIconCircleStyle(
                    'host',
                    location.pathname === '/host'
                  )}
                  onMouseEnter={() => setHoveredIcon('host')}
                  onMouseLeave={() => setHoveredIcon(null)}
                >
                  <LuCrown style={{ fontSize: 20 }} />
                </div>

                <span
                  style={getLabelStyle(
                    location.pathname === '/host'
                  )}
                >
                  Host
                </span>
              </div>
            )}

            {/* NOTIFICATIONS — shown here when the Host tab is hidden */}
            {!showHostNav && (
              <div
                style={iconWrapperStyleMobile}
                onClick={handleNotificationsClick}
              >
                <div
                  style={{
                    ...getIconCircleStyle(
                      'notifications',
                      notificationsSelected
                    ),
                    position: 'relative',
                  }}
                  onMouseEnter={() =>
                    setHoveredIcon('notifications')
                  }
                  onMouseLeave={() => setHoveredIcon(null)}
                >
                  <LuBell style={{ fontSize: 20 }} />

                  {unreadCount > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        minWidth: 18,
                        height: 18,
                        padding: '0 4px',
                        borderRadius: 999,
                        backgroundColor: 'red',
                        border: '1px solid #fff',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >
                      {displayUnread}
                    </div>
                  )}
                </div>

                <span
                  style={getLabelStyle(notificationsSelected)}
                >
                  {notificationLabel}
                </span>
              </div>
            )}

            {/* PROFILE */}
            <div
              style={iconWrapperStyleMobile}
              onClick={handleProfileClick}
              onMouseEnter={() => setHoveredIcon('profile')}
              onMouseLeave={() => setHoveredIcon(null)}
            >
              <div
                style={{
                  ...getIconCircleStyle(
                    'profile',
                    profileSelected
                  ),
                  width: 34,
                  height: 34,
                  overflow: 'hidden',
                  border: profileSelected
                    ? '2px solid #ddd'
                    : '2px solid #E7E9EE',
                  padding: 0,
                }}
              >
                {fullAvatarUrl ? (
                  <img
                    src={fullAvatarUrl}
                    alt="Your profile"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: '50%',
                    }}
                    onError={(e) => {
                      e.currentTarget.src =
                        '/default-avatar.png';
                    }}
                  />
                ) : (
                  <img
                    src="/default-avatar.png"
                    alt="Your profile"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </div>

              <span style={getLabelStyle(profileSelected)}>
                Profile
              </span>
            </div>
          </div>
        </div>

        <div style={bottomLineStyle} />
      </div>
    );
  }

  // ------------------------------------------------------------
  // DESKTOP / NON-MOBILE LAYOUT
  // ------------------------------------------------------------

  return (
    <div
      id="mainNavbar"
      style={{
        width: '100%',
        backgroundColor: theme.colors.background,
        fontFamily: theme.typography.fontFamilyBase,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 35,
          padding: `0 ${theme.spacing.md}px`,
        }}
      >
        {/* LEFT SECTION */}
        {variant === 'standard' && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={onLogoClick}
            >
              <TribeLogo
                style={{ width: '70px', height: 'auto' }}
              />
            </div>

            <div style={{ marginLeft: 35 }}>
              <LocationField
                selectedMarketId={selectedMarketId}
                onMarketSelected={handleMarketSelected}
              />
            </div>
          </>
        )}

        {variant === 'centered' && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={onBackClick}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  background: '#ddd',
                  borderRadius: 2,
                  marginRight: theme.spacing.xs,
                }}
              />

              <span
                style={{
                  fontSize: theme.typography.fontSizeSm,
                  color: theme.colors.textMain,
                }}
              >
                {backLabel}
              </span>
            </div>
          </>
        )}

        {/* RIGHT SECTION */}
        {variant === 'standard' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              paddingRight: '4px',
              height: 35,
            }}
          >
            {/* DISCOVER */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: theme.spacing.lg,
                cursor: 'pointer',
                position: 'relative',
              }}
              onClick={onDiscoverClick}
              onMouseEnter={() => setHoveredIcon('discover')}
              onMouseLeave={() => setHoveredIcon(null)}
            >
              <div
                style={getIconCircleStyle(
                  'discover',
                  discoverSelected
                )}
              >
                <IoIosSearch style={{ fontSize: 14 }} />
              </div>

              <span style={getLabelStyle(discoverSelected)}>
                {discoverLabel}
              </span>
            </div>

            {/* FRIENDS */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: theme.spacing.lg,
                cursor: 'pointer',
                position: 'relative',
              }}
              onClick={onFriendsClick}
              onMouseEnter={() => setHoveredIcon('friends')}
              onMouseLeave={() => setHoveredIcon(null)}
            >
              <div
                style={getIconCircleStyle(
                  'friends',
                  friendsSelected
                )}
              >
                <IoPeopleOutline style={{ fontSize: 18 }} />
              </div>

              <span style={getLabelStyle(friendsSelected)}>
                {friendsLabel}
              </span>
            </div>

            {/* NOTIFICATIONS */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: theme.spacing.lg,
                cursor: 'pointer',
                position: 'relative',
              }}
              onClick={onNotificationsClick}
              onMouseEnter={() =>
                setHoveredIcon('notifications')
              }
              onMouseLeave={() => setHoveredIcon(null)}
            >
              <div
                style={{
                  ...getIconCircleStyle(
                    'notifications',
                    false
                  ),
                  position: 'relative',
                }}
              >
                <IoIosNotificationsOutline
                  style={{ fontSize: 18 }}
                />

                {unreadCount > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: 'red',
                      color: '#fff',
                      fontSize: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {displayUnread}
                  </div>
                )}
              </div>

              <span style={getLabelStyle(false)}>
                {notificationLabel}
              </span>
            </div>
          </div>
        )}
      </div>

      <div style={bottomLineStyle} />
    </div>
  );
}